import { CircleAlert, ImageOff, NotebookPen, Printer, ScrollText, ShieldCheck } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "wouter";
import { toast } from "sonner";
import { dashboardApi } from "../api";
import { useT } from "../i18n";
import { useServiceText } from "../service-text";
import type { MonthReviewData } from "../types";
import { formatDate, monthLabel } from "../utils";
import Amount from "../components/Amount";
import { TableSkeleton } from "../components/Skeleton";

/**
 * Your month in review (backlog item 9).
 *
 * WHY IT IS NOT THE STATEMENT, and not the quarterly pack. The statement answers
 * "what did this cost"; the quarterly pack is built for a meeting. This one
 * answers "what did you get" — so it leads with the WORK and keeps the money to
 * three figures. A client opening this at month end should see what was made for
 * them, not a ledger.
 *
 * It shares the quarterly pack's assembler exactly (`buildReviewPack` over a
 * one-month period), so the two can never report different figures for the same
 * weeks. Different presentation, one source of numbers.
 *
 * A MONTH THAT HAS NOT ENDED SAYS SO. "Auto-assembled at month end" means a
 * client can open it on the 3rd, and a running month dressed as a finished
 * summary tells them their month is over when it is not.
 */
export default function MonthReviewPage({ month }: { month: string }) {
  const t = useT();
  const st = useServiceText();
  const [data, setData] = useState<MonthReviewData | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (which: string) => {
    setLoading(true);
    try {
      setData(await dashboardApi.monthReview(which));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.buildReview"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(month); }, [load, month]);

  if (loading && !data) {
    return <div className="page-stack"><TableSkeleton rows={4} label={t("common.loading")} /></div>;
  }
  if (!data) return null;

  const { pack, complete, closed } = data;
  const inContract = pack.months.length > 0;

  return (
    <div className="page-stack review-page">
      <section className="catalog-summary">
        <div>
          <span className="section-eyebrow">{t("monthReview.eyebrow")}</span>
          <h2>{t("monthReview.title", { month: monthLabel(month) })}</h2>
          <p>{t("monthReview.intro")}</p>
        </div>
        {/* No month picker here. The shell already has one, constrained to the
            contract's own months, and two controls doing the same job on one
            screen is a question the reader has to answer before they can read
            anything. The quarterly page keeps its own picker because a QUARTER is
            a thing the shell cannot select. */}
        <div className="statement-actions no-print">
          <button type="button" className="button button-secondary" onClick={() => window.print()}>
            <Printer size={16} /> {t("common.print")}
          </button>
        </div>
      </section>

      {!inContract && (
        <div className="review-notice review-notice-hard">
          <CircleAlert size={17} /><span>{t("monthReview.outside")}</span>
        </div>
      )}

      {inContract && !complete && (
        // Said before any figure is read, because every figure below is provisional.
        <div className="review-notice">
          <CircleAlert size={17} /><span>{t("monthReview.running", { month: monthLabel(month) })}</span>
        </div>
      )}

      {inContract && complete && closed && (
        <div className="review-notice review-notice-final">
          <ShieldCheck size={17} /><span>{t("monthReview.closedNote")}</span>
        </div>
      )}

      {inContract && (
        <>
          {/* THREE figures, not a ledger. The money detail lives on the statement,
              and the link below says so rather than duplicating it here. */}
          <section className="metric-grid month-review-metrics">
            <article className="metric-card">
              <span>{t("monthReview.delivered")}</span>
              <strong>{pack.delivered.length}</strong>
            </article>
            <article className="metric-card">
              <span>{t("monthReview.used")}</span>
              <strong><Amount value={pack.totals.spent} unit={t("common.cr")} /></strong>
            </article>
            <article className="metric-card">
              <span>{t("monthReview.carried")}</span>
              <strong><Amount value={pack.totals.closing} unit={t("common.cr")} /></strong>
            </article>
          </section>

          <section className="panel">
            <h3>{t("monthReview.theWork")}</h3>
            {pack.delivered.length ? (
              <div className="review-grid">
                {pack.delivered.map((item) => (
                  <article key={item.id} className="review-card">
                    <div className="review-card-image">
                      {item.cover_url
                        ? <img src={item.cover_url} alt="" loading="lazy" />
                        : <span className="review-card-placeholder"><ImageOff size={20} /></span>}
                    </div>
                    <div className="review-card-copy">
                      <strong dir="auto">{item.title}</strong>
                      <span dir="auto">{st.name(item.service_id, item.service_name)}</span>
                      <em>{item.completed_at ? formatDate(item.completed_at) : ""}</em>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <ImageOff size={26} />
                <h4>{t("monthReview.nothingYet")}</h4>
                <p>{t("monthReview.nothingYetBody")}</p>
              </div>
            )}
          </section>

          {pack.byCategory.length > 0 && (
            <section className="panel">
              <h3>{t("monthReview.effort")}</h3>
              <div className="category-bars">
                {pack.byCategory.map((row) => (
                  <div className="category-bar-row" key={row.category}>
                    <div>
                      <span>{st.category(row.category)}</span>
                      <strong><Amount value={row.credits} unit={t("common.cr")} /></strong>
                    </div>
                    <div className="bar-track">
                      <span style={{ inlineSize: `${percent(row.credits, pack.byCategory[0].credits)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {pack.stillOpen.length > 0 && (
            <section className="panel">
              <h3>{t("monthReview.running_work")}</h3>
              <p className="record-meta">{t("monthReview.runningBody")}</p>
              <ul className="document-list">
                {pack.stillOpen.map((row) => (
                  <li key={row.id}>
                    <strong dir="auto">{row.title}</strong>
                    <span className="record-meta"><Amount value={row.credits} unit={t("common.cr")} /></span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="panel">
            <h3>{t("monthReview.talked")}</h3>
            {pack.meetings.length || pack.decisions.length ? (
              <ol className="record-list">
                {pack.meetings.map((meeting) => (
                  <li key={meeting.id}>
                    <div className="record-when">{formatDate(meeting.held_on)}</div>
                    <div className="record-body">
                      <strong dir="auto"><NotebookPen size={13} /> {meeting.title}</strong>
                      <p dir="auto">{meeting.summary}</p>
                    </div>
                  </li>
                ))}
                {pack.decisions.map((decision) => (
                  <li key={decision.id}>
                    <div className="record-when">{formatDate(decision.decided_on)}</div>
                    <div className="record-body">
                      <strong dir="auto"><ScrollText size={13} /> {decision.title}</strong>
                      <p dir="auto">{decision.rationale}</p>
                    </div>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="record-meta">{t("monthReview.nothingTalked")}</p>
            )}
          </section>

          {/* The money lives on the statement. Pointing at it beats reprinting it
              here, where a second copy could disagree. */}
          <p className="month-review-more no-print">
            <Link href="/statement">{t("monthReview.seeStatement")} →</Link>
          </p>
        </>
      )}
    </div>
  );
}

/** Relative to the LARGEST row, not the total — with nine categories a
 *  share-of-total bar is a sliver for every one and compares nothing. */
function percent(value: number, largest: number): number {
  if (!largest) return 0;
  return Math.max(3, Math.round((value / largest) * 100));
}
